import { useState } from "react";
import { Link } from "react-router";

import { Trash2Icon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useEventGroupRepository } from "@/pages/event-groups/hooks/use-event-group-repository";
import type { EventGroup } from "@/types/event-group";

export type DeleteGroupDialogProps = {
  group: EventGroup;
  /** The group's member count, for the copy: the events stay in Mood. */
  eventCount: number;
  /** A plain delete went through: the page leaves for the groups list. */
  onDone: () => void;
};

const FAILED = "Could not delete the group. Try again in a moment.";

/** What happens to the group's events. A group with none says so, and one event reads as one. */
function eventsSentence(count: number): string {
  if (count === 0) return "It has no events.";
  if (count === 1)
    return "Its 1 event stays in Mood; it just leaves the group.";
  return `Its ${count} events stay in Mood; they just leave the group.`;
}

/**
 * The Delete button of a group page, and the confirm behind it. A Tina group (one with an `externalKey`, the id of its
 * festival) has no Delete here: it is taken out of Mood by Unpublish on the festival's page, where the curator sees
 * what goes with it, so this sends them there.
 */
export function DeleteGroupDialog({
  group,
  eventCount,
  onDone,
}: DeleteGroupDialogProps) {
  const { remove } = useEventGroupRepository();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (group.externalKey) {
    return (
      <div className="flex items-center gap-2">
        <p className="max-w-xs text-sm text-muted-foreground">
          Tina made this group for a festival. Unpublish it on the
          festival&apos;s page, where you see what goes with it.
        </p>
        <Button asChild variant="outline">
          <Link to={`/tina/festivals/${encodeURIComponent(group.externalKey)}`}>
            Open the festival
          </Link>
        </Button>
      </div>
    );
  }

  async function confirm() {
    setRunning(true);
    setError(null);
    try {
      const result = await remove(group);
      if (!result.ok) {
        setError(result.message);
        setRunning(false);
        return;
      }
      // The page leaves, so the confirm stays off until it does.
      onDone();
    } catch (caught) {
      setError(toHumanErrorMessage(caught) || FAILED);
      setRunning(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!running) setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2Icon className="size-4" />
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this group?</DialogTitle>
          <DialogDescription>
            This deletes the group. {eventsSentence(eventCount)}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={running}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={running}
            onClick={() => void confirm()}
          >
            Delete group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
